<template>
  <el-dialog
    title="故障单详情"
    :visible="visible"
    width="900px"
    append-to-body
    @close="$emit('update:visible', false)"
  >
    <div
      v-loading="loading"
      class="detail-container"
    >
      <template v-if="detail">
        <!-- 步骤进度条 -->
        <div class="step-wrapper">
          <el-steps
            :active="getStepActive(detail)"
            finish-status="success"
            align-center
          >
            <el-step title="发现" />
            <el-step :title="isReportPartyFlow ? '已上报待处理' : '待处理'" />
            <el-step title="已维修" />
          </el-steps>
        </div>

        <div class="section-title">
          基础信息
        </div>
        <div class="custom-desc">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                故障编号：
              </div>
              <div class="desc-value">
                <code class="highlight-code">{{ detail.faultCode }}</code>
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">
                处理状态：
              </div>
              <div class="desc-value">
                <el-tag
                  :type="getStatusType(detail.status)"
                  size="small"
                  effect="light"
                >
                  {{ detail.statusName || detail.status }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                设备名称：
              </div>
              <div class="desc-value">
                <span class="device-name-text">{{ detail.deviceName }}</span>
                <el-tag v-if="detail.deviceSource === 'external'" size="mini" class="ml-2">非系统设备</el-tag>
                <el-tag
                  v-if="detail.deviceLevel === 'A'"
                  type="danger"
                  size="mini"
                  effect="dark"
                  class="ml-2"
                >
                  重要设备
                </el-tag>
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">
                消防系统：
              </div>
              <div class="desc-value">
                {{ detail.systemName || '-' }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                设备编号：
              </div>
              <div class="desc-value">
                {{ detail.deviceCode || '-' }}
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">
                设施类型：
              </div>
              <div class="desc-value">
                {{ detail.deviceTypeName || '-' }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                设备所属区域：
              </div>
              <div class="desc-value">
                {{ detail.areaPath || '-' }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                设备具体位置：
              </div>
              <div class="desc-value">
                {{ detail.installLocation || '-' }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                录入来源：
              </div>
              <div class="desc-value">
                {{ detail.faultSourceName || detail.faultSource }}
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">
                故障级别：
              </div>
              <div class="desc-value">
                <el-tag
                  :type="getFaultLevelType(detail.faultLevel)"
                  size="small"
                  effect="light"
                >
                  {{ detail.faultLevelName || detail.faultLevel }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                发现人：
              </div>
              <div class="desc-value">
                {{ detail.foundUserName }}
              </div>
            </div>
            <div class="desc-item">
              <div class="desc-label">
                发现时间：
              </div>
              <div class="desc-value">
                {{ formatDateTime(detail.foundTime) }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">
                故障情况描述：
              </div>
              <div class="desc-value desc-value--multiline">
                {{ formatFaultDesc(detail.faultDesc) }}
              </div>
            </div>
          </div>
        </div>

        <!-- 已上报待处理：现场图并入「上报信息」展示，避免与下方重复 -->
        <template v-if="hasFaultImages && !isReportPartyFlow">
          <div class="section-title mt-4">
            现场图片
          </div>
          <ImageThumbViewer
            :file-ids="detail.faultImages"
            img-alt="现场图片"
          />
        </template>

        <!-- 分配信息，当有分配为个人或者小组，并且未完成维修时显示 -->
        <template v-if="detail.assignType && detail.assignType !== 'none' && detail.status !== 'fixed'">
          <div class="section-title mt-4">
            分配信息
          </div>

          <div class="custom-desc">
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">
                  分配方式：
                </div>
                <div class="desc-value">
                  {{ detail.assignTypeName || detail.assignType }}
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">
                  {{ detail.assignType === 'user' ? '执行人员' : '执行小组' }}
                </div>
                <div class="desc-value">
                  <el-tooltip
                    v-if="detail.assignType === 'user' && getAssignTargetText(detail) !== '-'"
                    :content="getAssignTargetText(detail)"
                    placement="top"
                    effect="dark"
                  >
                    <span class="ellipsis-text">{{ getAssignTargetText(detail) }}</span>
                  </el-tooltip>
                  <!-- 小组分配且存在组内委派时，委派说明换行展示，避免与小组名挤在同一行 -->
                  <div
                    v-else-if="detail.assignType === 'team'"
                    class="team-assign-value"
                  >
                    <span>{{ getAssignTargetText(detail) }}</span>
                    <span
                      v-if="detail.teamAssigneeName"
                      class="team-assign-delegate-hint"
                    >组内委派：{{ detail.teamAssigneeName }}</span>
                  </div>
                  <span v-else>{{ getAssignTargetText(detail) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 已上报待处理：展示上报 Word 所用补充信息（与处置弹窗只读区一致） -->
        <template v-if="isReportPartyFlow">
          <div class="section-title mt-4">
            上报信息
            <el-tag
              v-if="detail.reportTime"
              type="info"
              size="mini"
              style="margin-left: 8px; font-weight: normal"
            >
              上报时间：{{ formatDateTime(detail.reportTime) }}
            </el-tag>
          </div>

          <div class="custom-desc">
            <div class="desc-row">
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  故障原因
                </div>
                <div class="desc-value desc-value--multiline">
                  {{ formatFaultDesc(reportContentForRead.faultDesc || detail.faultDesc) }}
                </div>
              </div>
            </div>
            <div
              v-if="hasFaultImages"
              class="desc-row"
            >
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  现场照片
                </div>
                <div class="desc-value desc-value--thumb">
                  <ImageThumbViewer
                    :file-ids="detail.faultImages"
                    variant="compact"
                    img-alt="现场照片"
                    preview-title="查看照片"
                    preview-width="640px"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="hasReportExtraImages"
              class="desc-row"
            >
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  补充现场照片
                </div>
                <div class="desc-value desc-value--thumb">
                  <ImageThumbViewer
                    :file-ids="reportExtraImageFileIds"
                    variant="compact"
                    img-alt="补充现场照片"
                    preview-title="查看照片"
                    preview-width="640px"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="reportContentForRead.proposed"
              class="desc-row"
            >
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  建议方案
                </div>
                <div class="desc-value desc-value--multiline">
                  {{ reportContentForRead.proposed }}
                </div>
              </div>
            </div>
            <div
              v-if="reportContentForRead.emergency"
              class="desc-row"
            >
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  应急处置
                </div>
                <div class="desc-value desc-value--multiline">
                  {{ reportContentForRead.emergency }}
                </div>
              </div>
            </div>
            <div
              v-if="reportContentForRead.wbUser || reportContentForRead.wbUserPhone"
              class="desc-row"
            >
              <div class="desc-item">
                <div class="desc-label">
                  维保人员
                </div>
                <div class="desc-value">
                  {{ reportContentForRead.wbUser || '-' }}
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">
                  手机号
                </div>
                <div class="desc-value">
                  {{ reportContentForRead.wbUserPhone || '-' }}
                </div>
              </div>
            </div>
            <div
              v-if="reportContentForRead.xfkUser || reportContentForRead.xfkUserPhone"
              class="desc-row"
            >
              <div class="desc-item">
                <div class="desc-label">
                  消防科人员
                </div>
                <div class="desc-value">
                  {{ reportContentForRead.xfkUser || '-' }}
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">
                  手机号
                </div>
                <div class="desc-value">
                  {{ reportContentForRead.xfkUserPhone || '-' }}
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">
                  上报人
                </div>
                <div class="desc-value">
                  {{ detail.reportUserName || '-' }}
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div
                class="desc-item"
                style="flex: 1"
              >
                <div class="desc-label">
                  上报文件：
                </div>
                <div class="desc-value">
                  <el-button
                    type="primary"
                    plain
                    size="small"
                    icon="el-icon-download"
                    :loading="downloadingReportFile"
                    @click="handleDownloadReportedFile"
                  >
                    下载上报文件
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 处置信息 -->
        <template v-if="detail.handleType">
          <div class="section-title mt-4">
            处置详情
          </div>

          <div class="custom-desc">
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">
                  处置方式：
                </div>
                <div class="desc-value">
                  {{ detail.handleTypeName || detail.handleType }}
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">
                  处置时间
                </div>
                <div class="desc-value">
                  {{ formatDateTime(detail.handleTime) }}
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">
                  处置人员
                </div>
                <div class="desc-value">
                  {{ detail.handleUserName || '-' }}
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">
                  维修完成时间
                </div>
                <div class="desc-value">
                  {{ detail.fixTime ? formatDateTime(detail.fixTime) : '-' }}
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">
                  处置描述
                </div>
                <div class="desc-value">
                  {{ detail.handleDesc || '-' }}
                </div>
              </div>
            </div>
          </div>

          <template v-if="hasHandleImages">
            <div class="section-title mt-4">
              处置后图片
            </div>
            <ImageThumbViewer
              :file-ids="detail.handleImages"
              img-alt="处置后图片"
            />
          </template>
        </template>
      </template>
    </div>

    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">
        关闭
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { faultService } from './fault.service';
// 工具函数
import { parseImageFileIds, uniqueFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { formatFaultDesc } from '@/features/gnmk/fault/utils/faultHandleUtils.js';

export default {
  name: 'FaultDetailDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    faultId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      detail: null,
      /** 下载已生成的上报 Word */
      downloadingReportFile: false
    };
  },
  computed: {
    hasFaultImages () {
      return this.detail && parseImageFileIds(this.detail.faultImages).length > 0;
    },
    hasHandleImages () {
      return this.detail && parseImageFileIds(this.detail.handleImages).length > 0;
    },
    /**
     * 是否「上报甲方」流程：以处置方式 handleType === report 为准（完成维修结案后服务端会写入）。
     * 正式上报后若历史数据未落 handleType，则以 status === reported 兜底。
     */
    isReportPartyFlow () {
      const d = this.detail;
      if (!d) return false;
      if (d.handleType === 'report') return true;
      if (d.status === 'reported') return true;
      return false;
    },
    /** 解析详情中的 reportContent（上报 Word 补充项 JSON） */
    parsedReportContent () {
      const rc = this.detail && this.detail.reportContent;
      if (!rc) return {};
      try {
        return JSON.parse(rc);
      } catch (e) {
        return {};
      }
    },
    /** 与处置弹窗只读区一致：兼容历史字段名 */
    reportContentForRead () {
      const p = this.parsedReportContent;
      return {
        faultDesc: p.faultDesc || '',
        proposed: p.proposed || p.suggestedAction || '',
        emergency: p.emergency || p.reportDetail || '',
        wbUser: p.wbUser || p.repairUnit || '',
        wbUserPhone: p.wbUserPhone || '',
        xfkUser: p.xfkUser || p.contactInfo || '',
        xfkUserPhone: p.xfkUserPhone || ''
      };
    },
    /** 补充现场图 fileId，供 ImageThumbViewer 拉取缩略图 */
    reportExtraImageFileIds () {
      const p = this.parsedReportContent;
      const raw = p && p.reportExtraImages;
      if (raw == null) return '';
      if (typeof raw === 'string') {
        return uniqueFileIds(raw.split(',').map(s => s.trim()).filter(Boolean)).join(',');
      }
      if (Array.isArray(raw)) {
        return uniqueFileIds(raw.filter(Boolean)).join(',');
      }
      return '';
    },
    hasReportExtraImages () {
      return parseImageFileIds(this.reportExtraImageFileIds).length > 0;
    }
  },
  methods: {
    formatFaultDesc,
    loadDetail () {
      this.loading = true;
      faultService.getFaultDetail(this.faultId).then(res => {
        this.detail = res.data;
      }).finally(() => {
        this.loading = false;
      });
    },
    getStepActive (d) {
      if (!d) return 0;
      // 三步：发现(0) → 录入待处理(1) → 已维修(2)；A 级与普通设备一致
      if (d.status === 'fixed') return 3;
      if (d.status === 'reported') return 2;
      return 1;
    },
    getFaultLevelType (l) {
      return { urgent: 'danger', normal: 'warning', resolved: 'success' }[l] || '';
    },
    getStatusType (s) {
      return { pending: 'warning', reported: 'info', fixed: 'success' }[s] || '';
    },
    getAssignTargetText (detail) {
      if (!detail || !detail.assignType || detail.assignType === 'none') return '-';
      if (detail.assignType === 'team') return detail.assignTeamName || detail.assignTeamId || '-';
      return detail.assignUserNames || detail.assignUserIds || '-';
    },
    /** 下载服务端已存档的上报 Word（与处置弹窗一致） */
    handleDownloadReportedFile () {
      if (!this.faultId) return;
      this.downloadingReportFile = true;
      faultService.downloadReportedFile(this.faultId).then(res => {
        downloadFromApiBlobResponse(res, '故障上报单.docx');
      }).finally(() => {
        this.downloadingReportFile = false;
      });
    },
    formatDateTime
  },
  created () {
    this.loadDetail();
  }
};
</script>

<style lang="scss" scoped>
.detail-container {
  padding: 0 4px;
}

/* 顶部流程条 */
.step-wrapper {
  background: #f8f9fb;
  padding: 24px 10px 10px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
}

/* 标题样式 */
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}
.mt-4 { margin-top: 20px; }

/* 现代化描述列表核心样式 (纯 CSS 实现) */
.custom-desc {
  border: 1px solid #ebeef5;
  border-radius: 6px; /* 圆角外框 */
  overflow: hidden;
  font-size: 14px;
  background: #fff;
}

.desc-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.desc-item {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }
}

.desc-label {
  width: 120px;
  background-color: #f8f9fb; /* 柔和的浅灰背景 */
  color: #606266;
  padding: 10px 6px 10px 12px;
  box-sizing: border-box;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.desc-value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  box-sizing: border-box;
  word-break: break-all;
  display: flex;
  align-items: center;
  overflow: hidden;
}

/* 细节排版辅助 */
.highlight-code {
  color: #409eff;
  background: #ecf5ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}
.device-name-text { font-weight: 500; }
.ml-2 { margin-left: 8px; }

/* 针对多行文本的特殊处理 */
.desc-value--multiline {
  white-space: pre-wrap; /* 让换行符 \n 生效 */
  line-height: 1.6;      /* 增加行高，让多行阅读更舒适 */
  align-items: flex-start; /* 多行文本推荐顶部对齐，覆盖原有的居中对齐 */
  padding-top: 5px;     /* 配合 flex-start 微调视觉居中 */
  padding-bottom: 5px;
}

/* 上报信息内缩略图行：与文本块一致顶部对齐 */
.desc-value--thumb {
  align-items: flex-start;
  padding-top: 8px;
  padding-bottom: 8px;
}

/* 指定人员可能很长：单行省略，悬停展示完整文本 */
.ellipsis-text {
  flex: 1;
  min-width: 0;
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: normal;
}

/* 执行小组：名称与「委派给」分两行 */
.team-assign-value {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 4px;
}

.team-assign-delegate-hint {
  color: #409eff;
  line-height: 1.4;
  white-space: normal;
  word-break: break-all;
}

</style>
